import { useEffect, useRef, useState } from "react";
import { useAmendDecision } from "../../hooks/useAmendDecision";
import { useDraft } from "../../hooks/useDraft";
import { isRunInFlight, useGlobalRunStore } from "../../hooks/useGlobalRunStore";
import { DraftRefusedError, type DraftRefusal, useSubmitAnswer } from "../../hooks/useQueues";
import { useSettleGuard } from "../../hooks/useSettleGuard";
import {
  AMEND_REPLACES_LEAD,
  AMEND_REPLACES_TAIL,
  AMEND_SAVED_LINE,
  SAVE_AMENDMENT_ACT,
} from "../../lib/reviewLines";
import { stripAnchors } from "../../lib/stripAnchors";
import { asString, cardKeyAct } from "./shared";
import type { components } from "../../api/types";

type Node = components["schemas"]["Node"];

// The amend's founder lines live in lib/reviewLines.ts and its call in
// hooks/useAmendDecision.ts, the one amend the spec drawer makes too.

// saveErrorLine is the page's own "Couldn't save" line through a GUARDED read:
// useSubmitAnswer throws what toSaveError (hooks/useQueues) makes of a failed
// POST - an Error carrying the founder's line (a refusal, DraftRefusedError,
// renders as the page's notice instead of here) - or, for a thrown value
// toSaveError does not know, that value unchanged, so an unguarded
// error.error.message could throw during render (and the app has no error
// boundary).
function saveErrorLine(err: unknown): string {
  const envelopeMessage = (err as { error?: { message?: unknown } } | null | undefined)?.error
    ?.message;
  if (typeof envelopeMessage === "string" && envelopeMessage !== "") {
    return `Couldn't save: ${envelopeMessage}`;
  }
  if (err instanceof Error && err.message !== "") return `Couldn't save: ${err.message}`;
  return "Couldn't save.";
}

// ---- Covered-by holder row (0148) -----------------------------------
//
// A question whose standing answer the engine found already said by a
// confirmed decision carries fields.covered_by_candidate_id (the holder). The
// founder rules on it here: Yes (COVERED_BY - the question settles on that
// decision at the next run's S1) or No (amend that decision with their answer,
// through the shipped 0143 amend endpoint). The row plays back BOTH texts in
// full: the served answer, and the holder's title and value from the questions
// queue's served context (0051 S1c; the tree threads ids only, so the holder's
// prose is never duplicated into it).

// The COVERED_BY pick sentinel - the mirror of planner.CoveredByPickID.
// Trace-invariant T28 (orchestrator/internal/contract/trace_invariants/
// t28_test.go) pins this literal, and QuestionCard.tsx's covered_by_candidate_id
// field read, against the Go constants; it reads the whole Review surface
// (views/Review.tsx and components/review/*.tsx).
const COVERED_BY_PICK = "COVERED_BY";

// useCoveredRowHolder keeps the row mounted with its "Saved." line for the rest
// of the mount after an amend 2xx (A26 + A29): the amend withdraws the question
// from the served ids, so the queue refetch it triggers would otherwise drop
// `offered` - and the outcome line with it.
export function useCoveredRowHolder(offered?: Node): {
  holder: Node | undefined;
  amended: boolean;
  onAmendSaved: (holder: Node) => void;
} {
  const [amendedHolder, setAmendedHolder] = useState<Node>();
  return {
    holder: amendedHolder ?? offered,
    amended: amendedHolder !== undefined,
    onAmendSaved: setAmendedHolder,
  };
}

// coveredYesHeldByEdit is the row's unsaved-edit gate (A16 (2)): Yes rules on
// the SAVED answer (the served node's fields.answer), so it is held while the
// panel's own text differs from it, up to surrounding whitespace. The row reads
// it for its Yes, and the authority panel for its marked hint (0051 S1b), so
// the hint never points at a held Yes.
export function coveredYesHeldByEdit(localAnswer: string, node: Node): boolean {
  return localAnswer.trim() !== asString(node.fields?.answer).trim();
}

export function CoveredByHolderRow({
  productId,
  node,
  holder,
  localAnswer,
  onUseSavedAnswer,
  onActNext,
  hasSavedDraft,
  amended,
  onAmendSaved,
  onRefused,
}: {
  productId: string;
  node: Node;
  holder: Node;
  // The panel's own (possibly unsaved, localStorage-seeded) answer text; a
  // server cannot see an unsaved local edit, so the row gates Yes on it.
  localAnswer: string;
  onUseSavedAnswer: () => void;
  // 0051 S1c: the panel's act-advance - a saved Yes moves on through it, so
  // the page knows the move was an act's.
  onActNext: () => void;
  hasSavedDraft: boolean;
  amended: boolean;
  onAmendSaved: (holder: Node) => void;
  // 0159 F8: a Yes the server refused (409 draft.covered_by_refused) shows its
  // line in the Review page's notice, not as this row's save-error line (the
  // Yes hook's onRefused option, the question its item, so it lands even if
  // the row has unmounted). The item carries the holder's title, so the notice
  // can name the decision this row played back (refusalNoticeLine words the
  // line).
  onRefused: (refusal: DraftRefusal) => void;
}) {
  const fields = (node.fields ?? {}) as Record<string, unknown>;
  // The SERVED node's answer: it carries any saved-but-unfolded draft (the
  // served tree is draft-overlaid), and the overlay drops the marker whenever
  // that draft differs from the stored answer, so while this row renders it
  // equals the stored answer up to TrimSpace.
  const serverAnswer = asString(fields.answer);
  const holderValue = stripAnchors(asString(holder.fields?.value));
  // A35: the mid-run signal, read from the app's global run store itself - it
  // refreshes with no fetch of its own and survives navigation onto this page.
  const runInFlight = useGlobalRunStore((s) => isRunInFlight(s.runs, productId));
  const yes = useSubmitAnswer(productId, {
    item: { id: node.id, title: node.title, holderTitle: holder.title },
    onRefused,
  });
  // 0074, 0051 S1c: Yes rules in one tap, so after a move an act caused shows
  // this card it takes no tap or key for the settle time, and it never takes
  // the click that ends a press it did not see begin (the founder's ruling of
  // 2026-10-19); after the founder's own navigation it is not held.
  const yesGuard = useSettleGuard({ afterActMove: true });
  const [amendOpen, setAmendOpen] = useState(false);
  // The amendment is a PERSISTED DRAFT like every other Review textarea:
  // Review's window listener fires inside textareas by design (Escape and
  // Cmd/Ctrl+Arrow navigate), and a navigation away and back restores it.
  // Keyed by the node AND the holder, so text typed against one holder is
  // never offered, or posted, against another (both render sites also key the
  // row on holder.id, because useDraft reads its key only at mount).
  const [amendText, setAmendText, clearAmendText] = useDraft(
    productId,
    node.id + "-covered-amend-" + holder.id,
    serverAnswer,
  );
  // The amend, through the one hook the spec drawer uses too (the refusal
  // before posting, the POST, its invalidations, the in-flight rule). The row
  // has no other in-flight signal, so its refusal line also shows while the
  // run is in flight (ownInFlightSignal false).
  const { amend, errorLine: amendErrorLine } = useAmendDecision(productId, {
    onSaved: () => {
      // The text now lives on the server as the carrier's answer.
      clearAmendText();
      // Collapse IN PLACE to the saved line for the rest of the mount; the
      // act does not advance.
      onAmendSaved(holder);
    },
    ownInFlightSignal: false,
  });

  const amendTrimmed = amendText.trim();
  // The amend's ONE submit: the Save amendment button and every Cmd/Ctrl+Enter
  // path below call it, so the pending guard here and the refusal before
  // posting (in the hook) hold on each.
  const submitAmend = () => {
    if (amendTrimmed === "" || amend.isPending) return;
    amend.mutate({ decisionId: holder.id, answerText: amendTrimmed });
  };
  // A30: while the amend panel is open, Cmd/Ctrl+Enter is the amend's own act.
  // It is taken (preventDefault + stopPropagation) so it never reaches
  // QuestionPanel's or AuthorityForkPanel's window listener, which would save
  // the main draft, or the stored KEEP/REVISE choice, and advance - throwing
  // the amendment away. Taken whether or not the amend text is blank (a blank
  // one submits nothing). The key is read through cardKeyAct (shared.tsx), the
  // one spelling Review's keyboard guard reads too.
  const takeAmendKey = (e: {
    key: string;
    metaKey: boolean;
    ctrlKey: boolean;
    target: EventTarget | null;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) => {
    if (!amendOpen || cardKeyAct(e) !== "submit") return;
    e.preventDefault();
    e.stopPropagation();
    submitAmend();
  };

  // Opening the panel moves focus into the amend textarea: a click leaves focus
  // on the toggle (Chrome, Firefox) or on body (Safari), where the textarea's
  // own Cmd/Ctrl+Enter never runs. (A ref and an effect on the open state, not
  // the focus-on-mount attribute.)
  const rowRef = useRef<HTMLDivElement>(null);
  const amendTextareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (amendOpen) amendTextareaRef.current?.focus();
  }, [amendOpen]);

  // The row root's onKeyDown takes the key wherever focus sits INSIDE the row
  // (the textarea, the toggle, Save amendment, Yes). A keystroke with NOTHING
  // focused targets body, outside React's root container, so no React handler
  // can see it: focus drops there after a click on a control Safari does not
  // focus, or when a focused button disables while the amend is pending. That
  // keystroke is the amend's too - but only while the founder's last press
  // since opening the panel was inside the row: a press on the page's own
  // controls (a KEEP/REVISE row, a pick, the question) leaves the key to the
  // page, exactly as with the panel closed. The document listener runs before
  // every window listener (the bubble path reaches document first), so
  // stopping it there is enough. The mousedown tracking mirrors RunMenu's
  // outside-click listener; like the panels' own listeners, the effect
  // re-registers on every render so it calls the current takeAmendKey.
  const lastPressInRow = useRef(true);
  useEffect(() => {
    if (!amendOpen || amended) return;
    const onDown = (e: MouseEvent) => {
      lastPressInRow.current =
        e.target instanceof Element && (rowRef.current?.contains(e.target) ?? false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.target === document.body && lastPressInRow.current) takeAmendKey(e);
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  });

  if (amended) {
    return (
      <div
        data-testid="covered-by-holder-row"
        className="mt-6 rounded-lg border border-ink-700 bg-ink-900/40 px-5 py-4"
      >
        <p className="text-[14px] text-ink-200 leading-relaxed">
          {AMEND_SAVED_LINE}
        </p>
      </div>
    );
  }

  // A16 (2) THE UNSAVED-EDIT GATE: Yes rules on the SAVED answer, so it waits
  // while the panel holds an edit the server has not seen. The amend control is
  // not gated.
  const dirty = coveredYesHeldByEdit(localAnswer, node);

  const onYes = () => {
    if (dirty || yes.isPending) return;
    yes.mutate(
      {
        questionId: node.id,
        answer: serverAnswer,
        selectedPick: COVERED_BY_PICK,
        // A32: the holder the row DISPLAYED; the resolver refuses the act
        // unless it still equals the question's marker at S1.
        coveredByHolderId: holder.id,
      },
      // The act advances, like Save & next (review decision N2). A refused Yes
      // does not; the page's notice says why (0159 F8, the hook's onRefused).
      { onSuccess: () => onActNext() },
    );
  };

  // amendErrorLine is the amend hook's failure line: an in-flight refusal reads
  // the shared rule's line, any other failure its founder copy.

  return (
    <div
      ref={rowRef}
      data-testid="covered-by-holder-row"
      onKeyDown={takeAmendKey}
      className="mt-6 rounded-lg border border-ink-700 bg-ink-900/40 px-5 py-4"
    >
      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
        does this already say what you said?
      </p>
      <p className="border-l-2 border-ink-700 pl-3 text-[14px] text-ink-200 leading-relaxed whitespace-pre-wrap">
        {serverAnswer}
      </p>
      <div className="mt-4">
        <p className="text-[14px] font-medium text-ink-100 leading-relaxed">{holder.title}</p>
        {holderValue && (
          <p className="mt-1 text-[14px] text-ink-300 leading-relaxed whitespace-pre-wrap">
            {holderValue}
          </p>
        )}
      </div>

      {/* A26 + A35: hidden while a run is in flight - a draft the running S1
          already folded stays listed until the run's success cleanup, so the
          line would be false for it. */}
      {hasSavedDraft && !runInFlight && (
        <p className="mt-4 text-[13px] text-ink-400 leading-relaxed">
          You already saved an answer here. Saving again replaces it.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => yesGuard.onClick(onYes)}
          onPointerDown={yesGuard.onPointerDown}
          onPointerUp={yesGuard.onPointerUp}
          onPointerCancel={yesGuard.onPointerCancel}
          onKeyDown={yesGuard.onKeyDown}
          disabled={dirty || yes.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-1.5 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        >
          Yes - it's already covered
        </button>
        <button
          type="button"
          onClick={() => {
            // The press that opens (or closes) the panel is inside the row.
            lastPressInRow.current = true;
            setAmendOpen((open) => !open);
          }}
          aria-expanded={amendOpen}
          className="inline-flex items-center gap-2 rounded-md border border-ink-700 hover:border-ink-500 px-3.5 py-1.5 text-sm font-medium text-ink-100 transition-colors"
        >
          No - amend that decision with my answer
        </button>
      </div>

      {dirty && (
        <div className="mt-3">
          <p className="text-[13px] text-ink-400 leading-relaxed">
            You've edited your answer since you saved it. Save the new version, or use your saved answer to rule on it here.
          </p>
          <button
            type="button"
            onClick={onUseSavedAnswer}
            className="mt-1 text-[13px] text-accent underline-offset-2 hover:underline"
          >
            Use my saved answer
          </button>
        </div>
      )}

      {yes.error != null && !(yes.error instanceof DraftRefusedError) && (
        <p className="mt-3 text-sm text-danger">{saveErrorLine(yes.error)}</p>
      )}

      <p className="mt-3 font-mono text-[11px] text-ink-500">
        This saves your choice.
      </p>

      {amendOpen && (
        <div className="mt-4">
          <p className="text-[13px] text-ink-300 leading-relaxed">
            {AMEND_REPLACES_LEAD}<strong className="font-medium text-ink-100">"{holder.title}"</strong>{AMEND_REPLACES_TAIL}{" "}
            <strong className="font-medium text-ink-100">
              This does not settle this question.
            </strong>
          </p>
          <div className="mt-3 rounded-lg border border-ink-700 hover:border-ink-600 focus-within:border-accent/60 bg-ink-900/60 transition-colors">
            <textarea
              ref={amendTextareaRef}
              rows={4}
              value={amendText}
              onChange={(e) => setAmendText(e.target.value)}
              // W4: readOnly, never disabled - a disabled focused textarea
              // loses focus, and a second Cmd/Ctrl+Enter would then target body
              // and reach both window listeners.
              readOnly={amend.isPending}
              // A30: this textarea owns its Cmd/Ctrl+Enter (takeAmendKey, the
              // same handler as the row root's). Stopping the synthetic event
              // stops the native one at React's root container, before
              // QuestionPanel's / AuthorityForkPanel's window listeners. Every
              // other key still bubbles.
              onKeyDown={takeAmendKey}
              className="w-full resize-none bg-transparent px-5 py-3 text-sm text-ink-100 placeholder:text-ink-500 focus:outline-none"
            />
            <div className="flex items-center justify-end gap-3 border-t border-ink-800 px-3 py-2">
              <button
                type="button"
                onClick={submitAmend}
                disabled={amendTrimmed === "" || amend.isPending}
                className="rounded-md bg-accent px-3 py-1 text-xs font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                {amend.isPending ? "saving…" : SAVE_AMENDMENT_ACT}
              </button>
            </div>
          </div>
          {amendErrorLine && (
            <p className="mt-3 text-sm text-danger">{amendErrorLine}</p>
          )}
        </div>
      )}
    </div>
  );
}
